import { useUpdateMerchantMutation } from '@/store/api/services/merchants/merchants';
import { useGetMerchantEnumsQuery } from '@/store/api/services/resources/enums';
import type { Merchant } from '@/types';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

const EditMerchantSchema = z.object({
  company_name: z.string().min(1, 'Company name is required'),
  owner_phone_number: z.string().min(1, 'Phone is required'),
  owner_email: z.string().email('Invalid email address'),
  owner_first_name: z.string().min(1, 'First name is required'),
  owner_last_name: z.string().min(1, 'Last name is required'),
  is_serviceable: z.boolean(),
  classification: z.enum(['single_store', 'corporate']),
  notes: z.string().optional(),
});

type EditMerchantFormData = z.infer<typeof EditMerchantSchema>;

interface MerchantEditDetailsProps extends ModalChildrenProps {
  props: {
    merchant?: Merchant;
    onSuccess?: () => void;
  } | null;
}

const MerchantEditDetails = ({ hide, props }: MerchantEditDetailsProps) => {
  // Early return if props or merchant is null/undefined
  if (!props?.merchant) {
    return null;
  }

  const { merchant, onSuccess } = props;

  const [updateMerchant, { isLoading }] = useUpdateMerchantMutation();

  // Fetch merchant enums for classification values
  const { data: merchantEnumsData } = useGetMerchantEnumsQuery();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditMerchantFormData>({
    resolver: zodResolver(EditMerchantSchema),
    defaultValues: {
      company_name: merchant.company_name || '',
      owner_phone_number: merchant.owner_phone_number || '',
      owner_email: merchant.owner_email || '',
      owner_first_name: merchant.owner_first_name || '',
      owner_last_name: merchant.owner_last_name || '',
      is_serviceable: merchant.is_serviceable ?? false,
      classification: merchant.classification || 'single_store',
      notes: merchant.notes || '',
    },
  });

  // Reset form when component unmounts (handles close via click outside, escape, etc.)
  useEffect(() => {
    return () => {
      if (merchant) {
        reset({
          company_name: merchant.company_name || '',
          owner_phone_number: merchant.owner_phone_number || '',
          owner_email: merchant.owner_email || '',
          owner_first_name: merchant.owner_first_name || '',
          owner_last_name: merchant.owner_last_name || '',
          is_serviceable: merchant.is_serviceable ?? false,
          classification: merchant.classification || 'single_store',
          notes: merchant.notes || '',
        });
      }
    };
  }, [reset, merchant]);

  const handleClose = () => {
    if (merchant) {
      reset({
        company_name: merchant.company_name || '',
        owner_phone_number: merchant.owner_phone_number || '',
        owner_email: merchant.owner_email || '',
        owner_first_name: merchant.owner_first_name || '',
        owner_last_name: merchant.owner_last_name || '',
        is_serviceable: merchant.is_serviceable ?? false,
        classification: merchant.classification || 'single_store',
        notes: merchant.notes || '',
      });
    }
    hide();
  };

  const onSubmit = async (data: EditMerchantFormData) => {
    if (!merchant) {
      return;
    }

    try {
      await updateMerchant({
        id: merchant.id,
        ...data,
      }).unwrap();

      toast.success('Merchant updated successfully');

      if (onSuccess) {
        onSuccess();
      }
      hide();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update merchant');
      console.error('Error updating merchant:', error);
    }
  };

  const merchantDisplayId = merchant.id;

  return (
    <>
      <DialogContent sx={{ py: 4 }}>
        <Stack gap={2}>
          {/* Merchant ID (read-only) */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Merchant ID
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {merchantDisplayId}
            </Typography>
          </Box>

          {/* Company Name */}
          <Controller
            name="company_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Company Name"
                fullWidth
                size="small"
                error={!!errors.company_name}
                helperText={errors.company_name?.message}
              />
            )}
          />

          {/* Phone and Email */}
          <Stack direction="row" gap={2}>
            <Controller
              name="owner_phone_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone"
                  fullWidth
                  size="small"
                  error={!!errors.owner_phone_number}
                  helperText={errors.owner_phone_number?.message}
                />
              )}
            />

            <Controller
              name="owner_email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="email"
                  fullWidth
                  size="small"
                  type="email"
                  error={!!errors.owner_email}
                  helperText={errors.owner_email?.message}
                />
              )}
            />
          </Stack>

          {/* First Name and Last Name */}
          <Stack direction="row" gap={2}>
            <Controller
              name="owner_first_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  size="small"
                  error={!!errors.owner_first_name}
                  helperText={errors.owner_first_name?.message}
                />
              )}
            />

            <Controller
              name="owner_last_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  size="small"
                  error={!!errors.owner_last_name}
                  helperText={errors.owner_last_name?.message}
                />
              )}
            />
          </Stack>

          {/* Is Serviceable Toggle */}
          <Controller
            name="is_serviceable"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} onChange={field.onChange} color="primary" />}
                label="Is Serviceable"
              />
            )}
          />

          {/* Classification */}
          <Controller
            name="classification"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Classification</InputLabel>
                <Select {...field} label="Classification" error={!!errors.classification}>
                  {merchantEnumsData?.data?.classifications?.map((classification) => {
                    // Normalize enum value to lowercase with underscores (e.g., "SINGLE_STORE" -> "single_store")
                    const normalizedValue = classification.toLowerCase().replace(/\s+/g, '_');
                    // Create display label by splitting on underscores and capitalizing each word
                    const displayLabel = normalizedValue
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');
                    return (
                      <MenuItem key={classification} value={normalizedValue}>
                        {displayLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          />

          {/* Notes */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Notes
            </Typography>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextareaAutosize
                  {...field}
                  minRows={4}
                  maxRows={8}
                  placeholder="Enter notes about this merchant..."
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    padding: '12px 14px',
                    border: errors.notes ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                  }}
                />
              )}
            />
            {errors.notes && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.notes.message}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          loading={isLoading}
          disabled={isLoading}
        >
          Save changes
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default MerchantEditDetails;
